import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, Plus, Edit, Eye,
  BookOpen, Clock, Users, Tag, FileText,
  Link, Download, CheckCircle, X, AlertCircle, Save,
  EyeOff, Eye as EyeIcon, ChevronDown, ChevronUp,
  Settings, Target, Globe, Video, FileImage,
  RefreshCw, Trash2, AlertTriangle,
  ArrowUpDown, ArrowUp, ArrowDown, Archive
} from 'lucide-react';
import trainingService from '../../../api/services/trainingService';
import { courseTypeService } from '../../../api/crm/config/configService';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import toast from 'react-hot-toast';
import Button from '../../../components/common/Button/Button';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { formatDate } from '../../../utils/stringUtils';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../../utils/formValidation';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import useUrlTableState from '../../../hooks/useUrlTableState';

const TrainingCatalog = () => {
  const navigate = useNavigate();

  // Capitalize function for display values
  const capitalizeValue = (value) => {
    if (!value) return '';

    // Handle special cases
    const specialCases = {
      'soft_skills': 'Soft Skills',
      'all_employees': 'All Employees',
      'super_admin': 'Super Admin',
      'hr_manager': 'HR Manager',
      'hr_executive': 'HR Executive',
      'sales_manager': 'Sales Manager',
      'sales_executive': 'Sales Executive',
      'dispatch_manager': 'Dispatch Manager',
      'support_manager': 'Support Manager'
    };

    if (specialCases[value]) {
      return specialCases[value];
    }

    // Default capitalization
    return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
  };

  // Capitalize first letter of a word
  const capitalizeFirst = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Format duration from minutes to hours/minutes/seconds
  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) {
      return '0m';
    }
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 && hours === 0) parts.push(`${secs}s`);

    return parts.length > 0 ? parts.join(' ') : '0m';
  };

  const handleViewCourse = (courseId) => {
    console.log('View course:', courseId);
    // Set nav context so sidebar highlights Training & Development
    sessionStorage.setItem('trainingNavContext', 'training');
    navigate(`/hrms/training/course/${courseId}`);
  };


  const handlePreviewCourse = async () => {

    if (courseForm.title && typeof courseForm.title === 'string' && courseForm.title.trim() !== '') {

      // If editing a course, fetch modules and lessons
      if (selectedCourse && selectedCourse.id) {
        try {
          const structureResult = await trainingService.getCourseStructure(selectedCourse.id);
          if (structureResult.success && structureResult.data) {
            setPreviewCourseModules(structureResult.data.modules || []);
          } else {
            setPreviewCourseModules([]);
          }
        } catch (error) {
          console.error('Error fetching course structure:', error);
          setPreviewCourseModules([]);
        }
      } else {
        // For new courses, no modules yet
        setPreviewCourseModules([]);
      }

      setShowPreviewModal(true);
    } else {
      alert('Please fill in the course title before previewing.');
    }
  };

  // File validation function
  const validateFile = (file, allowedTypes, maxSizeMB = 100) => {
    const errors = [];

    // Check file size (convert MB to bytes)
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
      errors.push(`File is too large (${fileSizeMB}MB). Maximum allowed size is ${maxSizeMB}MB.`);
    }

    // Check file type
    if (allowedTypes && allowedTypes.length > 0) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const mimeType = file.type;

      const isAllowedByExtension = allowedTypes.some(type =>
        type.toLowerCase() === fileExtension
      );

      const isAllowedByMimeType = allowedTypes.some(type => {
        const mimeMap = {
          'pdf': 'application/pdf',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'mp4': 'video/mp4',
          'avi': 'video/avi',
          'mov': 'video/quicktime',
          'zip': 'application/zip',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif'
        };
        return mimeMap[type] === mimeType;
      });

      if (!isAllowedByExtension && !isAllowedByMimeType) {
        const fileExtension = file.name.split('.').pop().toUpperCase();
        errors.push(`File type "${fileExtension}" is not supported. Please upload a ${allowedTypes.join(', ').toUpperCase()} file.`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  };

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    filterType: 'all',
    filterDifficulty: 'all',
    filterDuration: 'all',
    filterStatus: 'all',
    filterStartDate: '',
    filterEndDate: ''
  }), []);

  const {
    filters: trainingFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateTrainingFilter,
    updatePage: updateTrainingPage,
    updateLimit: updateTrainingLimit,
    resetFilters: resetTrainingFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const selectedFilters = useMemo(() => ({
    type: trainingFilters.filterType || 'all',
    difficulty: trainingFilters.filterDifficulty || 'all',
    duration: trainingFilters.filterDuration || 'all',
    status: trainingFilters.filterStatus || 'all'
  }), [trainingFilters.filterType, trainingFilters.filterDifficulty, trainingFilters.filterDuration, trainingFilters.filterStatus]);

  const setSelectedFilters = useCallback((newFilters) => {
    updateTrainingFilter({
      filterType: newFilters.type,
      filterDifficulty: newFilters.difficulty,
      filterDuration: newFilters.duration,
      filterStatus: newFilters.status
    });
  }, [updateTrainingFilter]);

  const filterStartDate = trainingFilters.filterStartDate || '';
  const filterEndDate = trainingFilters.filterEndDate || '';

  const setDateRange = useCallback((range) => {
    updateTrainingFilter({ filterStartDate: range.startDate || '', filterEndDate: range.endDate || '' });
  }, [updateTrainingFilter]);

  const setFilterStartDate = useCallback((startDate) => {
    updateTrainingFilter({ filterStartDate: startDate });
  }, [updateTrainingFilter]);

  const setFilterEndDate = useCallback((endDate) => {
    updateTrainingFilter({ filterEndDate: endDate });
  }, [updateTrainingFilter]);

  const currentPage = trainingFilters.page;
  const itemsPerPage = trainingFilters.limit;

  const setCurrentPage = useCallback((page) => {
    updateTrainingPage(page);
  }, [updateTrainingPage]);

  const setItemsPerPage = useCallback((limit) => {
    updateTrainingLimit(limit);
  }, [updateTrainingLimit]);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [playingLesson, setPlayingLesson] = useState(null); // { title, type, url } for video modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [courseToUnpublish, setCourseToUnpublish] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [pagination, setPagination] = useState({});
  const [previewCourseModules, setPreviewCourseModules] = useState([]);

  // Export & Selection State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedRowsData, setSelectedRowsData] = useState(new Map()); // Stores full course objects: Map<id, course>
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Course Type Selection State
  const [courseTypeList, setCourseTypeList] = useState([]);
  const [loadingCourseTypes, setLoadingCourseTypes] = useState(false);

  // API Data State - initialize as null to prevent flashing
  const [courses, setCourses] = useState(null);

  // Validation functions
  const validateSearchTerm = (term) => {
    if (typeof term !== 'string') {
      return 'Search term must be a string';
    }
    if (term.length > 100) {
      return 'Search term must be less than 100 characters';
    }
    return null;
  };

  // Helper function for text input key press - allow approved symbols
  const handleTextKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  // Helper function for text input key press - allow approved symbols
  const handleCodeKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  // Helper function for number input key down (positive numbers only, 0 and greater)
  const handleNumberKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow numbers on top of keyboard
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      // Allow numbers on numpad
      (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Block: minus, plus, e, E, period (for decimal)
    if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
      e.preventDefault();
    }
  };

  const validateFilters = (filters) => {
    const errors = {};

    // Validate type filter - Allow 'all' or any non-empty string for dynamic course types
    if (filters.type && typeof filters.type !== 'string') {
      errors.type = 'Invalid course type selected';
    }

    // Validate difficulty filter
    const validDifficulties = ['all', 'beginner', 'intermediate', 'advanced'];
    if (filters.difficulty && !validDifficulties.includes(filters.difficulty)) {
      errors.difficulty = 'Invalid difficulty level selected';
    }

    // Validate duration filter - match the actual UI options (lowercase)
    const validDurations = ['all', 'under 1 hour', '1-2 hours', '2-4 hours', 'over 4 hours'];
    if (filters.duration && !validDurations.includes(filters.duration)) {
      errors.duration = 'Invalid duration selected';
    }

    // Validate status filter - match the actual UI options
    const validStatuses = ['all', 'published', 'draft', 'archived'];
    if (filters.status && !validStatuses.includes(filters.status)) {
      errors.status = 'Invalid status selected';
    }

    return errors;
  };

  const validateCourseForm = (form) => {
    const errors = {};

    // Validate title (Required)
    if (!form.title || typeof form.title !== 'string' || form.title.trim().length === 0) {
      errors.title = 'Course title is required and cannot be empty';
    } else if (form.title.trim().length < 3) {
      errors.title = 'Course title must be at least 3 characters long';
    } else if (form.title.trim().length > 100) {
      errors.title = 'Course title cannot exceed 100 characters';
    } else if (!VALID_TEXT_REGEX.test(form.title.trim())) {
      errors.title = 'Course title can only contain letters, numbers, and approved symbols';
    }

    // Validate code (Required)
    if (!form.code || typeof form.code !== 'string' || form.code.trim().length === 0) {
      errors.code = 'Course code is required and cannot be empty';
    } else if (form.code.trim().length < 3) {
      errors.code = 'Course code must be at least 3 characters long';
    } else if (form.code.trim().length > 20) {
      errors.code = 'Course code cannot exceed 20 characters';
    } else if (!VALID_TEXT_REGEX.test(form.code.trim())) {
      errors.code = 'Course code can only contain letters, numbers, and approved symbols';
    }

    // Validate summary (Required)
    if (!form.summary || typeof form.summary !== 'string' || form.summary.trim().length === 0) {
      errors.summary = 'Course summary is required and cannot be empty';
    } else if (form.summary.trim().length < 10) {
      errors.summary = 'Course summary must be at least 10 characters long';
    } else if (form.summary.trim().length > 1500) {
      errors.summary = 'Course summary cannot exceed 1500 characters';
    } else if (!VALID_TEXT_REGEX.test(form.summary.trim())) {
      errors.summary = 'Course summary can only contain letters, numbers, and approved symbols';
    }

    // Validate objectives (Required)
    if (!form.objectives || form.objectives.length === 0) {
      errors.objectives = 'At least one learning objective is required';
    } else if (form.objectives.length > 10) {
      errors.objectives = 'Maximum 10 learning objectives allowed';
    } else if (form.objectives.some(obj => !obj || typeof obj !== 'string' || obj.trim().length === 0)) {
      errors.objectives = 'All learning objectives must be filled in';
    } else if (form.objectives.some(obj => typeof obj === 'string' && obj.trim().length < 5)) {
      errors.objectives = 'Each learning objective must be at least 5 characters long';
    } else if (form.objectives.some(obj => typeof obj === 'string' && obj.trim().length > 200)) {
      errors.objectives = 'Each learning objective cannot exceed 200 characters';
    } else if (form.objectives.some(obj => typeof obj === 'string' && !VALID_TEXT_REGEX.test(obj.trim()))) {
      errors.objectives = 'Learning objectives can only contain letters, numbers, and approved symbols';
    }

    // Validate prerequisites
    if (form.prerequisites && typeof form.prerequisites === 'string') {
      if (form.prerequisites.trim().length > 300) {
        errors.prerequisites = 'Prerequisites cannot exceed 300 characters';
      } else if (form.prerequisites.trim().length > 0 && !VALID_TEXT_REGEX.test(form.prerequisites.trim())) {
        errors.prerequisites = 'Prerequisites can only contain letters, numbers, and approved symbols';
      }
    }

    // Duration is now calculated from lessons - no validation needed

    // Validate difficulty (Required)
    const validDifficulties = ['beginner', 'intermediate', 'advanced'];
    if (!form.difficulty || !validDifficulties.includes(form.difficulty)) {
      errors.difficulty = 'Please select a valid difficulty level';
    }

    // Validate type (Required)
    if (!form.courseType) {
      errors.courseType = 'Course Type is required';
    }

    // Tags removed - not needed for production

    return errors;
  };

  // Field-level validation
  const validateField = (name, value) => {
    let error = null;
    switch (name) {
      case 'title':
        if (!value || typeof value !== 'string' || value.trim().length === 0) {
          error = 'Course title is required and cannot be empty';
        } else if (value.trim().length < 3) {
          error = 'Course title must be at least 3 characters long';
        } else if (value.trim().length > 100) {
          error = 'Course title cannot exceed 100 characters';
        } else if (!VALID_TEXT_REGEX.test(value.trim())) {
          error = 'Course title can only contain letters, numbers, and approved symbols';
        }
        break;
      case 'code':
        if (!value || typeof value !== 'string' || value.trim().length === 0) {
          error = 'Course code is required and cannot be empty';
        } else if (value.trim().length < 3) {
          error = 'Course code must be at least 3 characters long';
        } else if (value.trim().length > 20) {
          error = 'Course code cannot exceed 20 characters';
        } else if (!VALID_TEXT_REGEX.test(value.trim())) {
          error = 'Course code can only contain letters, numbers, and approved symbols';
        }
        break;
      case 'summary':
        if (!value || typeof value !== 'string' || value.trim().length === 0) {
          error = 'Course summary is required and cannot be empty';
        } else if (value.trim().length < 10) {
          error = 'Course summary must be at least 10 characters long';
        } else if (value.trim().length > 1500) {
          error = 'Course summary cannot exceed 1500 characters';
        } else if (!VALID_TEXT_REGEX.test(value.trim())) {
          error = 'Course summary can only contain letters, numbers, and approved symbols';
        }
        break;
      case 'prerequisites':
        if (value && typeof value === 'string') {
          if (value.trim().length > 300) {
            error = 'Prerequisites cannot exceed 300 characters';
          } else if (value.trim().length > 0 && !VALID_TEXT_REGEX.test(value.trim())) {
            error = 'Prerequisites can only contain letters, numbers, and approved symbols';
          }
        }
        break;
      case 'difficulty':
        const validDifficulties = ['beginner', 'intermediate', 'advanced'];
        if (!value || !validDifficulties.includes(value)) {
          error = 'Please select a valid difficulty level';
        }
        break;
      case 'courseType':
        if (!value) {
          error = 'Course Type is required';
        }
        break;
      case 'objectives':
        if (!Array.isArray(value) || value.length === 0) {
          error = 'At least one learning objective is required';
        } else if (value.some(obj => !obj || typeof obj !== 'string' || obj.trim().length === 0)) {
          error = 'All learning objectives must be filled in';
        } else if (value.some(obj => typeof obj === 'string' && obj.trim().length < 5)) {
          error = 'Each learning objective must be at least 5 characters long';
        } else if (value.some(obj => typeof obj === 'string' && obj.trim().length > 200)) {
          error = 'Each learning objective cannot exceed 200 characters';
        } else if (value.some(obj => typeof obj === 'string' && !VALID_TEXT_REGEX.test(obj.trim()))) {
          error = 'Learning objectives can only contain letters, numbers, and approved symbols';
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setValidationErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Load course types and courses
  // Load course types and courses
  useEffect(() => {
    fetchActiveCourseTypes();
    loadCourses();
  }, [searchTerm, selectedFilters, filterStartDate, filterEndDate, currentPage, itemsPerPage]);

  // Reset selection when filters change (but not page)
  useEffect(() => {
    setSelectedRows(new Set());
    setSelectedRowsData(new Map());
  }, [searchTerm, selectedFilters]);

  const fetchActiveCourseTypes = async () => {
    setLoadingCourseTypes(true);
    try {
      const result = await courseTypeService.getAll({ activeOnly: true });
      if (result.responseCode === 200 || result.success) {
        setCourseTypeList(result.data || []);
      } else {
        toast.error('Failed to load course types');
        setCourseTypeList([]);
      }
    } catch (error) {
      console.error('Error fetching course types:', error);
      setCourseTypeList([]);
    } finally {
      setLoadingCourseTypes(false);
    }
  };

  // Load courses from API with comprehensive error handling
  const loadCourses = async () => {
    // Validate search term
    const searchError = validateSearchTerm(searchTerm);
    if (searchError) {
      setError(searchError);
      return;
    }

    // Validate filters
    const filterErrors = validateFilters(selectedFilters);
    if (Object.keys(filterErrors).length > 0) {
      setValidationErrors(filterErrors);
      setError('Please fix filter validation errors');
      return;
    }

    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      const queryParams = {
        search: searchTerm || '',
        type: selectedFilters.type || 'all',
        difficulty: selectedFilters.difficulty || 'all',
        duration: selectedFilters.duration || 'all',
        status: selectedFilters.status || 'all',
        startDate: filterStartDate || '',
        endDate: filterEndDate || '',
        page: currentPage || 1,
        limit: itemsPerPage || 10
      };

      const result = await trainingService.getCourses(queryParams);

      if (!result || typeof result !== 'object') {
        throw new Error('Invalid response format from courses API');
      }

      if (result.success) {
        const courses = result.data?.courses || [];
        const pagination = result.data?.pagination || {};

        // Validate that courses is an array
        if (Array.isArray(courses)) {
          setCourses(courses);
        } else {
          console.warn('Courses data is not an array:', courses);
          setCourses([]);
        }

        // Validate pagination object and sync currentPage
        if (typeof pagination === 'object' && pagination !== null) {
          setPagination(pagination);
          // Sync currentPage state with pagination object
          if (pagination.currentPage && pagination.currentPage !== currentPage) {
            setCurrentPage(pagination.currentPage);
          }
        } else {
          console.warn('Pagination data is not an object:', pagination);
          setPagination({});
        }
      } else {
        const errorMessage = result.error || 'Failed to load courses';
        setError(errorMessage);
        setCourses([]);
        setPagination({});
      }
    } catch (error) {
      console.error('Error loading courses:', error);
      setError('Failed to load courses. Please try again.');
      setCourses([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  };

  // Mock data removed - using API only

  // Filter options
  const filterOptions = {
    difficulty: ['All', 'Beginner', 'Intermediate', 'Advanced'],
    duration: ['All', 'Under 1 hour', '1-2 hours', '2-4 hours', 'Over 4 hours'],
    status: ['All', 'Published', 'Draft', 'Archived']
  };


  // Course form state - simplified for production
  const [courseForm, setCourseForm] = useState({
    title: '',
    code: '',
    summary: '',
    objectives: [''],
    prerequisites: '', // Optional
    difficulty: 'beginner',
    courseType: '',
    // passScore removed; defaults handled server-side
  });

  const sortOptions = [
    { value: 'title', label: 'Sort by Title' },
    { value: 'createdAt', label: 'Sort by Date' },
    { value: 'duration', label: 'Sort by Duration' },
    { value: 'level', label: 'Sort by Difficulty' }
  ];

  // Courses are already filtered by the API, but we sort them on the frontend
  const displayCourses = courses || [];

  const sortedCourses = useMemo(() => {
    if (!displayCourses) return [];
    if (!sortBy) return displayCourses;

    return [...displayCourses].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'type':
          aValue = (a.type || '').toLowerCase();
          bValue = (b.type || '').toLowerCase();
          break;
        case 'level':
          const difficultyOrder = { beginner: 1, intermediate: 2, advanced: 3 };
          aValue = difficultyOrder[a.difficulty] || 0;
          bValue = difficultyOrder[b.difficulty] || 0;
          break;
        case 'duration':
          aValue = Number(a.duration || 0);
          bValue = Number(b.duration || 0);
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'enrolledCount':
          aValue = Number(a.enrolledCount || 0);
          bValue = Number(b.enrolledCount || 0);
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [displayCourses, sortBy, sortOrder]);

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Build base export filters
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
      type: selectedFilters.type !== 'all' ? selectedFilters.type : undefined,
      difficulty: selectedFilters.difficulty !== 'all' ? selectedFilters.difficulty : undefined,
      duration: selectedFilters.duration !== 'all' ? selectedFilters.duration : undefined,
      status: selectedFilters.status !== 'all' ? selectedFilters.status : undefined,
      startDate: filterStartDate || undefined,
      endDate: filterEndDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, selectedFilters, filterStartDate, filterEndDate]);

  // Get current page course IDs
  const currentPageCourseIds = useMemo(
    () =>
      sortedCourses
        .map((c) => {
          const raw = c?._id ?? c?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [sortedCourses]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageCourseIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'training-catalog',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortBy || 'createdAt',
            sortOrder,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
            pdfFormat: format === 'pdf' ? 'a2' : undefined,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `training-catalog-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      sortBy,
      sortOrder,
      currentPageCourseIds,
    ]
  );

  // Handle export for selected courses with format selection
  const handleSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected courses to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  // Handle scoped export from modal
  const handleScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageCourseIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      sortedCourses.length,
      baseExportFilters,
      requestBackendExport,
      itemsPerPage,
      currentPageCourseIds,
    ]
  );

  // Export click handler
  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const effectivePaginationLimit = pagination?.limit || itemsPerPage;

  // Handlers with validation
  const handleSearch = (e) => {
    // Allow approved symbols
    const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);

    // Validate search term
    const searchError = validateSearchTerm(value);
    if (searchError) {
      setValidationErrors(prev => ({ ...prev, search: searchError }));
      setError(searchError);
      return;
    }

    setSearchTerm(value);
    setError(null);
    setValidationErrors(prev => ({ ...prev, search: null }));
  };

  // Helper function for search input key press - allow approved symbols
  const handleSearchKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  const handleFilterChange = (filterType, value) => {
    // Validate filter value
    const newFilters = {
      ...selectedFilters,
      [filterType]: value
    };

    const filterErrors = validateFilters(newFilters);
    if (Object.keys(filterErrors).length > 0) {
      setValidationErrors(prev => ({ ...prev, ...filterErrors }));
      setError('Invalid filter selection');
      return;
    }

    setSelectedFilters(newFilters);
    setError(null);
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(filterErrors).forEach(key => {
        delete newErrors[key];
      });
      return newErrors;
    });
  };

  const handleClearFilters = () => {
    console.log('Clearing all filters');
    resetTrainingFilters();
    setError(null);
    setValidationErrors({});
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;

    if (!isChecked) {
      // Clear all selections if unchecked
      setSelectedRows(new Set());
      setSelectedRowsData(new Map());
      return;
    }

    // Add current page items to selection if checked
    const newSelectedRows = new Set(selectedRows);
    const newSelectedRowsData = new Map(selectedRowsData);

    sortedCourses.forEach(course => {
      const id = course.id || course._id;
      newSelectedRows.add(id);
      newSelectedRowsData.set(id, course);
    });

    setSelectedRows(newSelectedRows);
    setSelectedRowsData(newSelectedRowsData);
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    const newSelectedData = new Map(selectedRowsData);

    if (newSelected.has(id)) {
      newSelected.delete(id);
      newSelectedData.delete(id);
    } else {
      newSelected.add(id);
      // Find the course object from current page data
      const course = sortedCourses.find(c => (c.id || c._id) === id);
      if (course) {
        newSelectedData.set(id, course);
      }
    }

    setSelectedRows(newSelected);
    setSelectedRowsData(newSelectedData);
  };

  const mapCoursesForExport = (coursesToMap) => {
    return coursesToMap.map(course => ({
      'Course ID': course.courseId || '',
      'Course Code': course.code || course.courseCode || '',
      'Title': course.title || '',
      'Summary': course.summary || course.description || '',
      'Course Type': typeof course.courseType === 'object' ? course.courseType?.name : (course.type || 'N/A'),
      'Difficulty': course.difficulty || course.level || '',
      'Duration (min)': course.duration || 0,
      'Status': course.status || '',
      'Enrolled Count': course.enrolledCount || 0,
      'Is Mandatory': course.isMandatory ? 'Yes' : 'No',
      'Instructor Name': typeof course.instructor === 'object' ? (course.instructor?.name || 'N/A') : (course.instructor || 'N/A'),
      'Created Date': course.createdAt ? formatDate(course.createdAt, { includeTime: true }) : 'N/A'
    }));
  };

  const handleSortSelect = (value) => {
    if (value === sortBy) return;
    setSortBy(value);
  };

  const handleSortOrderToggle = () => {
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
  };


  const resetForm = () => {
    setValidationErrors({});
    setTouched({});
    setError(null);
    setSelectedCourse(null);
    setCourseForm({
      title: '',
      code: '',
      summary: '',
      objectives: [''],
      prerequisites: '',
      difficulty: 'beginner',
      courseType: ''
    });
  };

  const handleCreateCourse = () => {
    resetForm();
    setShowCreateModal(true);
    // Scroll to top of modal when opening
    setTimeout(() => {
      const modal = document.querySelector('.modal-content');
      if (modal) {
        modal.scrollTop = 0;
      }
    }, 100);
  };

  const handleEditCourse = async (course) => {
    // Clear any existing validation errors
    setValidationErrors({});
    setError(null);
    setLoading(true);

    try {
      // Fetch complete course details from backend
      const result = await trainingService.getCourseById(course.id);
      if (result.success) {
        const courseData = result.data;
        setSelectedCourse(courseData);
        setCourseForm({
          title: courseData.title || '',
          code: courseData.courseCode || courseData.code || '',
          summary: courseData.description || courseData.summary || '',
          objectives: courseData.learningObjectives || courseData.objectives || [''],
          prerequisites: Array.isArray(courseData.prerequisites) ? courseData.prerequisites.join(', ') : (courseData.prerequisites || ''),
          difficulty: courseData.level || courseData.difficulty || 'beginner',
          courseType: typeof courseData.courseType === 'object' ? (courseData.courseType?._id || courseData.courseType?.id || '') : (courseData.courseType || ''),
          type: typeof courseData.courseType === 'object' ? (courseData.courseType?.name || 'Unknown') : 'Unknown'
        });
        setShowEditModal(true);
        // Scroll to top of modal when opening
        setTimeout(() => {
          const modal = document.querySelector('.modal-content');
          if (modal) {
            modal.scrollTop = 0;
          }
        }, 100);
      } else {
        setError(result.error || 'Failed to load course details');
      }
    } catch (error) {
      console.error('Error loading course details:', error);
      setError('Failed to load course details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCourse = async () => {
    // Validate form before submission
    const formErrors = validateCourseForm(courseForm);
    if (Object.keys(formErrors).length > 0) {
      setValidationErrors(formErrors);
      setError(null); // Clear general error, show field-specific errors

      // Mark all fields as touched
      const allTouched = Object.keys(courseForm).reduce((acc, key) => ({ ...acc, [key]: true }), {});
      setTouched(allTouched);

      // Auto-scroll to first validation error
      setTimeout(() => {
        const firstErrorField = Object.keys(formErrors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"], [data-field="${firstErrorField}"]`);

        if (errorElement) {
          // Scroll within the modal container
          const modalContainer = document.querySelector('.modal-content');
          if (modalContainer) {
            const elementRect = errorElement.getBoundingClientRect();
            const modalRect = modalContainer.getBoundingClientRect();
            const scrollTop = modalContainer.scrollTop + (elementRect.top - modalRect.top) - 100;
            modalContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
          } else {
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
          errorElement.focus();
        } else {
          const errorFields = document.querySelectorAll('[data-field]');
          for (let field of errorFields) {
            const fieldName = field.getAttribute('data-field');
            if (formErrors[fieldName]) {
              const modalContainer = document.querySelector('.modal-content');
              if (modalContainer) {
                const elementRect = field.getBoundingClientRect();
                const modalRect = modalContainer.getBoundingClientRect();
                const scrollTop = modalContainer.scrollTop + (elementRect.top - modalRect.top) - 100;
                modalContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
              } else {
                field.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
              }
              field.focus();
              break;
            }
          }
        }
      }, 100);

      return;
    }

    setLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      // Prepare course data with proper field mapping for backend API
      const courseData = {
        title: courseForm.title,
        courseCode: courseForm.code, // Add courseCode field
        description: courseForm.summary, // Map summary to description
        courseType: courseForm.courseType, // Use courseTypeId (ObjectId)
        level: courseForm.difficulty, // Map difficulty to level
        duration: 0, // Calculated from lessons automatically
        prerequisites: courseForm.prerequisites ? courseForm.prerequisites.split(',').map(p => p.trim()).filter(p => p.length > 0) : [],
        learningObjectives: courseForm.objectives.filter(obj => obj && obj.trim().length > 0) || [],
        instructor: {},
        status: showCreateModal ? 'draft' : (selectedCourse?.status || 'draft'),
        assessment: {
          passingScore: selectedCourse?.assessment?.passingScore ?? 70,
          maxAttempts: selectedCourse?.assessment?.maxAttempts ?? 3,
          timeLimit: selectedCourse?.assessment?.timeLimit ?? 0
        },
        certificate: {
          enabled: false,
          template: 'default',
          validityPeriod: 30 // Default validity period
        },
        pricing: {
          type: 'free',
          amount: 0,
          currency: 'USD'
        },
        isMandatory: false
      };

      let result;
      if (showCreateModal) {
        result = await trainingService.createCourse(courseData);
        if (result.success) {
          setShowCreateModal(false);
          resetForm();
          loadCourses(); // Reload courses
        } else {
          // If backend reports duplicate course code, surface it on the form field
          const msg = (result.error || '').toString();
          if (msg.toLowerCase().includes('course code') && msg.toLowerCase().includes('exist')) {
            setValidationErrors(prev => ({ ...prev, code: 'Course code already exists. Please choose a different code.' }));
          } else {
            setError(result.error || 'Failed to create course');
          }
        }
      } else {
        if (!selectedCourse || !selectedCourse.id) {
          setError('No course selected for update');
          return;
        }

        // If trying to set status to 'published', validate modules and lessons first
        if (courseData.status === 'published') {
          const courseStructure = await trainingService.getCourseStructure(selectedCourse.id);

          if (courseStructure.success && courseStructure.data) {
            const modules = courseStructure.data.modules || [];

            if (modules.length === 0) {
              setError('Course must have at least one module before publishing.');
              setLoading(false);
              return;
            }

            // Check if each module has at least one lesson
            const hasModulesWithoutLessons = modules.some(module => {
              const lessons = module.lessons || [];
              return lessons.length === 0;
            });

            if (hasModulesWithoutLessons) {
              setError('Some modules have no lessons. Add lessons to all modules before publishing.');
              setLoading(false);
              return;
            }
          }
        }

        result = await trainingService.updateCourse(selectedCourse.id, courseData);
        if (result.success) {
          setShowEditModal(false);
          resetForm();
          loadCourses(); // Reload courses
        } else {
          const msg = (result.error || '').toString();
          if (msg.toLowerCase().includes('course code') && msg.toLowerCase().includes('exist')) {
            setValidationErrors(prev => ({ ...prev, code: 'Course code already exists. Please choose a different code.' }));
          } else {
            setError(result.error || 'Failed to update course');
          }
        }
      }
    } catch (error) {
      console.error('Error saving course:', error);
      setError('Failed to save course. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishCourse = async (courseId) => {
    setLoading(true);
    try {
      // Validate that course has modules and lessons before attempting to publish
      const courseStructure = await trainingService.getCourseStructure(courseId);

      if (courseStructure.success && courseStructure.data) {
        const modules = courseStructure.data.modules || [];

        if (modules.length === 0) {
          setError('Course must have at least one module before publishing.');
          setLoading(false);
          return;
        }

        // Check if each module has at least one lesson
        const hasModulesWithoutLessons = modules.some(module => {
          const lessons = module.lessons || [];
          return lessons.length === 0;
        });

        if (hasModulesWithoutLessons) {
          setError('Some modules have no lessons. Add lessons to all modules before publishing.');
          setLoading(false);
          return;
        }
      }

      // If validation passes, proceed with publishing
      const result = await trainingService.publishCourse(courseId);
      if (result.success) {
        setCourses(prev => prev.map(course =>
          course.id === courseId
            ? { ...course, status: 'published', updatedAt: new Date().toISOString().split('T')[0] }
            : course
        ));
        setError(null);
      } else {
        setError(result.error || 'Failed to publish course');
      }
    } catch (error) {
      console.error('Error publishing course:', error);
      setError(error.message || 'Failed to publish course');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublishCourse = async (courseId, force = false) => {
    setLoading(true);
    try {
      const result = await trainingService.unpublishCourse(courseId, force);
      if (result.success) {
        setCourses(prev => prev.map(course =>
          course.id === courseId
            ? { ...course, status: 'draft', updatedAt: new Date().toISOString().split('T')[0] }
            : course
        ));
        setError(null);
        toast.success('Course unpublished successfully');
        setShowUnpublishConfirm(false);
        setCourseToUnpublish(null);
      } else if (result.isConflict || result.responseCode === 409) {
        setCourseToUnpublish(courseId);
        setShowUnpublishConfirm(true);
      } else {
        const errorMessage = result.error || result.responseMessage || 'Failed to unpublish course';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error unpublishing course:', error);
      const errorMessage = error.message || 'Failed to unpublish course. The course may have active assignments.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const confirmUnpublishCourse = async () => {
    if (!courseToUnpublish) return;
    await handleUnpublishCourse(courseToUnpublish, true);
  };

  const cancelUnpublishCourse = () => {
    setShowUnpublishConfirm(false);
    setCourseToUnpublish(null);
  };

  const handleBulkPublish = async () => {
    if (selectedRows.size === 0) return;
    const ids = Array.from(selectedRows);
    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    for (const courseId of ids) {
      try {
        // Validate structure before publishing
        const courseStructure = await trainingService.getCourseStructure(courseId);
        if (courseStructure.success && courseStructure.data) {
          const modules = courseStructure.data.modules || [];
          if (modules.length === 0) { failCount++; continue; }
          const hasModulesWithoutLessons = modules.some(m => (m.lessons || []).length === 0);
          if (hasModulesWithoutLessons) { failCount++; continue; }
        }
        const result = await trainingService.publishCourse(courseId);
        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (e) {
        failCount++;
      }
    }
    setLoading(false);
    if (successCount > 0) {
      toast.success(`${successCount} course(s) published successfully.`);
      loadCourses();
      setSelectedRows(new Set());
      setSelectedRowsData(new Map());
    }
    if (failCount > 0) {
      toast.error(`${failCount} course(s) could not be published (missing modules/lessons or already published).`);
    }
  };

  const handleBulkUnpublish = async () => {
    if (selectedRows.size === 0) return;
    const ids = Array.from(selectedRows);
    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    for (const courseId of ids) {
      try {
        const result = await trainingService.unpublishCourse(courseId);
        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (e) {
        failCount++;
      }
    }
    setLoading(false);
    if (successCount > 0) {
      toast.success(`${successCount} course(s) unpublished successfully.`);
      loadCourses();
      setSelectedRows(new Set());
      setSelectedRowsData(new Map());
    }
    if (failCount > 0) {
      toast.error(`${failCount} course(s) could not be unpublished.`);
    }
  };

  const handleArchiveCourse = async (courseId) => {
    setLoading(true);
    try {
      const result = await trainingService.archiveCourse(courseId);
      if (result.success) {
        setCourses(prev => prev.map(course =>
          course.id === courseId
            ? { ...course, status: 'archived', updatedAt: new Date().toISOString().split('T')[0] }
            : course
        ));
        setError(null);
      } else {
        setError(result.error || 'Failed to archive course');
      }
    } catch (error) {
      console.error('Error archiving course:', error);
      setError('Failed to archive course');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchiveCourse = async (courseId) => {
    setLoading(true);
    try {
      const result = await trainingService.unarchiveCourse(courseId);
      if (result.success) {
        setCourses(prev => prev.map(course =>
          course.id === courseId
            ? { ...course, status: 'draft', updatedAt: new Date().toISOString().split('T')[0] }
            : course
        ));
        setError(null);
      } else {
        setError(result.error || 'Failed to unarchive course');
      }
    } catch (error) {
      console.error('Error unarchiving course:', error);
      setError('Failed to unarchive course');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = (courseId) => {
    // Set the course to delete and show confirmation modal
    setCourseToDelete(courseId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCourse = async () => {
    if (!courseToDelete && !isBulkDelete) return;

    setLoading(true);
    setError(null);
    try {
      if (isBulkDelete) {
        // Bulk delete: iterate all selected IDs
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;
        for (const id of ids) {
          try {
            const result = await trainingService.deleteCourse(id);
            if (result.success) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (e) {
            failCount++;
          }
        }
        if (successCount > 0) {
          toast.success(`${successCount} course(s) deleted successfully.`);
          loadCourses();
          setSelectedRows(new Set());
          setSelectedRowsData(new Map());
        }
        if (failCount > 0) {
          toast.error(`${failCount} course(s) could not be deleted.`);
        }
        setShowDeleteConfirm(false);
        setIsBulkDelete(false);
      } else {
        // Single delete
        const result = await trainingService.deleteCourse(courseToDelete);
        if (result.success) {
          // Remove the course from the list
          setCourses(prev => prev.filter(course => course.id !== courseToDelete));
          setError(null);
          toast.success('Course deleted successfully');
          // Close confirmation modal
          setShowDeleteConfirm(false);
          setCourseToDelete(null);
        } else {
          const errorMessage = result.error || result.responseMessage || 'Failed to delete course';
          setError(errorMessage);
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      const errorMessage = error.message || 'Failed to delete course';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const cancelDeleteCourse = () => {
    setShowDeleteConfirm(false);
    setCourseToDelete(null);
    setIsBulkDelete(false);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteConfirm(true);
  };



  const handleManageStructure = (courseId) => {
    console.log('=== NAVIGATING TO COURSE STRUCTURE ===');
    console.log('CourseId being passed:', courseId);
    console.log('CourseId type:', typeof courseId);
    console.log('CourseId length:', courseId?.length);
    console.log('Navigating to:', `/modules_lessons/${courseId}`);
    navigate(`/modules_lessons/${courseId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'published': return 'Published';
      case 'draft': return 'Draft';
      case 'archived': return 'Archived';
      default: return status;
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-blue-100 text-blue-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyDisplay = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'Beginner';
      case 'intermediate': return 'Intermediate';
      case 'advanced': return 'Advanced';
      default: return difficulty;
    }
  };

  // Note: formatDuration is now defined above with proper hours/minutes/seconds formatting

  const totalCoursePages = Number(pagination?.totalPages || 0);
  const totalCourseCount = Number(pagination?.totalCourses || 0);
  const shouldShowPagination =
    !loading && totalCoursePages > 1 && totalCourseCount > effectivePaginationLimit;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Learning Catalog</h1>
          <p className="text-gray-600 mt-1">Manage learning content and paths</p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedRows.size > 0 && (
            <PermissionGate permission="training" action="update">
              <>
                <button
                  onClick={handleBulkPublish}
                  disabled={loading}
                  className="flex items-center px-3 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors text-sm disabled:opacity-50"
                  title="Publish selected courses"
                >
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                  Publish ({selectedRows.size})
                </button>
                <button
                  onClick={handleBulkUnpublish}
                  disabled={loading}
                  className="flex items-center px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm disabled:opacity-50"
                  title="Unpublish selected courses"
                >
                  <EyeOff className="h-4 w-4 mr-1.5" />
                  Unpublish ({selectedRows.size})
                </button>
              </>
            </PermissionGate>
          )}
          {selectedRows.size > 0 && (
            <PermissionGate permission="training" action="delete">
              <button
                onClick={handleBulkDelete}
                disabled={loading}
                className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
                title="Delete selected courses"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete ({selectedRows.size})
              </button>
            </PermissionGate>
          )}
          <PermissionGate permission="training" action="read">
            <div ref={selectedExportWrapRef} className="relative">
              <Button
                variant="outline"
                icon={exportLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className={selectedRows.size > 0 ? "bg-blue-50 border-blue-200 text-[#0F2942]" : ""}
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}
              </Button>
              {showInlineSelectedFormats && (
                <SelectedExportFormatBar
                  onFormatSelect={handleSelectedFormatExport}
                  loading={exportLoading}
                  onClose={() => setShowInlineSelectedFormats(false)}
                  onMouseEnter={() => setInlineFormatPanelEntered(true)}
                  onMouseLeave={() => setInlineFormatPanelEntered(false)}
                />
              )}
            </div>
          </PermissionGate>
          <PermissionGate permission="training" action="read">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-blue-50/30 transition-colors"
            >
              {viewMode === 'grid' ? <EyeIcon className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
              {viewMode === 'grid' ? 'List View' : 'Grid View'}
            </button>
          </PermissionGate>
          <PermissionGate permission="training" action="create">
            <button
              onClick={handleCreateCourse}
              className="flex items-center px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Course
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4 w-full">
          <div className="flex-shrink-0 flex items-center h-10 px-3 bg-gray-50 border border-gray-300 rounded-lg">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-brand focus:ring-brand h-4 w-4 cursor-pointer"
              checked={sortedCourses.length > 0 && sortedCourses.every(c => selectedRows.has(c.id || c._id))}
              onChange={handleSelectAll}
            />
          </div>

          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={handleSearch}
              onKeyPress={handleSearchKeyPress}
              maxLength={100}
              className="w-full h-10 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-0 focus:border-brand"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  const input = document.querySelector('input[placeholder="Search courses..."]');
                  if (input) input.focus();
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex-shrink-0 min-w-[300px]">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={setDateRange}
            />
          </div>

          {(searchTerm || filterStartDate || filterEndDate || Object.values(selectedFilters).some(filter => filter !== 'all')) && (
            <div className="relative flex-shrink-0">
              <PermissionGate permission="training" action="read">
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="h-10"
                >
                  Clear All
                </Button>
              </PermissionGate>
            </div>
          )}
        </div>

        {/* Filter Options */}
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={selectedFilters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-brand"
              >
                <option value="all">All Types</option>
                {courseTypeList.map((type) => (
                  <option key={type.id || type._id} value={type.id || type._id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
              <select
                value={selectedFilters.difficulty}
                onChange={(e) => handleFilterChange('difficulty', e.target.value.toLowerCase())}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-brand"
              >
                {filterOptions.difficulty.map(option => (
                  <option key={option} value={option.toLowerCase()}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <select
                value={selectedFilters.duration}
                onChange={(e) => handleFilterChange('duration', e.target.value.toLowerCase())}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-brand"
              >
                {filterOptions.duration.map(option => (
                  <option key={option} value={option.toLowerCase()}>{option}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={selectedFilters.status}
                onChange={(e) => handleFilterChange('status', e.target.value.toLowerCase())}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-brand"
              >
                {filterOptions.status.map(option => (
                  <option key={option} value={option.toLowerCase()}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-brand" />
          <span className="ml-2 text-gray-600">Loading courses...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Courses Grid/List */}
      {!loading && courses && (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedCourses.map((course) => (
                <div key={course.id} className={`bg-white rounded-xl border overflow-hidden hover:shadow-md transition-all relative ${selectedRows.has(course.id || course._id) ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50/10' : 'border-gray-200'}`}>
                  {/* Selection Checkbox */}
                  <div className="absolute top-6 left-6 z-10">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-brand focus:ring-brand h-4 w-4 cursor-pointer shadow-sm"
                      checked={selectedRows.has(course.id || course._id)}
                      onChange={() => handleSelectRow(course.id || course._id)}
                    />
                  </div>
                  {/* Course Header */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 pl-8">
                        <h3
                          className="text-lg font-semibold text-gray-900 mb-1 capitalize whitespace-normal break-words"
                          title={course.title}
                        >
                          {course.title}
                        </h3>
                        <p className="text-sm text-gray-600">{course.code}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(course.status)}`}>
                          {getStatusDisplay(course.status)}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4 line-clamp-2 capitalize">{course.summary}</p>

                    {/* Course Meta */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-2" />
                        {formatDuration(course.duration)}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Users className="h-4 w-4 mr-2" />
                        {course.enrolledCount} enrolled
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(course.tags || []).map((tag, index) => (
                        <span key={index} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded capitalize">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Difficulty and Type */}
                    <div className="flex items-center justify-between mb-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getDifficultyColor(course.difficulty)}`}>
                        {getDifficultyDisplay(course.difficulty)}
                      </span>
                      <span className="text-sm text-gray-500 capitalize">{capitalizeValue(course.type)}</span>
                    </div>
                  </div>

                  {/* Course Actions */}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <PermissionGate permission="training" action="update">
                          <button
                            onClick={() => handleEditCourse(course)}
                            className="text-[#0F2942] hover:text-blue-800 transition-colors"
                            title="Edit Course"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="training" action="read">
                          <button
                            onClick={() => handleViewCourse(course.id)}
                            className="text-brand hover:text-blue-800 transition-colors"
                            title="Preview Course"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="training" action="update">
                          <button
                            onClick={() => {
                              console.log('Settings button clicked for course:', course);
                              console.log('Course ID from course object:', course.id);
                              console.log('Full course object:', course);
                              handleManageStructure(course.id);
                            }}
                            className="text-indigo-600 hover:text-indigo-800 transition-colors"
                            title="Manage Structure"
                          >
                            <Settings className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>

                      <div className="flex items-center space-x-2">
                        {course.status === 'published' ? (
                          <PermissionGate permission="training" action="update">
                            <button
                              onClick={() => handleUnpublishCourse(course.id)}
                              className="text-yellow-600 hover:text-yellow-800 transition-colors"
                              title="Unpublish"
                              aria-label="Unpublish"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        ) : (
                          <PermissionGate permission="training" action="update">
                            <button
                              onClick={() => handlePublishCourse(course.id)}
                              className="text-brand hover:text-blue-800 transition-colors"
                              title="Publish"
                              aria-label="Publish"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        )}
                        {course.status === 'archived' ? (
                          <PermissionGate permission="training" action="update">
                            <button
                              onClick={() => handleUnarchiveCourse(course.id)}
                              className="text-[#0F2942] hover:text-blue-800 transition-colors"
                              title="Unarchive Course"
                              aria-label="Unarchive"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        ) : (
                          <PermissionGate permission="training" action="update">
                            {/* <button
                              onClick={() => handleArchiveCourse(course.id)}
                              className="text-gray-600 hover:text-gray-800 transition-colors"
                              title="Archive Course"
                            >
                              Archive
                            </button> */}
                          </PermissionGate>
                        )}
                        <PermissionGate permission="training" action="delete">
                          <button
                            onClick={() => handleDeleteCourse(course.id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Delete Course"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                    <tr>
                      <th className="px-6 py-3 text-left w-10">
                        {/* Master checkbox moved to common filter bar */}
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('title')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Course</span>
                          {sortBy === 'title' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('type')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Type</span>
                          {sortBy === 'type' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('level')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Difficulty</span>
                          {sortBy === 'level' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('duration')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Duration</span>
                          {sortBy === 'duration' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {sortBy === 'status' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('enrolledCount')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Enrolled</span>
                          {sortBy === 'enrolledCount' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Created Date</span>
                          {sortBy === 'createdAt' ? (
                            sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedCourses.map((course) => (
                      <tr key={course.id} className={`hover:bg-blue-50/30 ${selectedRows.has(course.id || course._id) ? 'bg-blue-50/50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-brand focus:ring-brand h-4 w-4"
                            checked={selectedRows.has(course.id || course._id)}
                            onChange={() => handleSelectRow(course.id || course._id, course)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div
                              className="text-sm font-medium text-gray-900 capitalize truncate max-w-[200px]"
                              title={course.title}
                            >
                              {course.title}
                            </div>
                            <div className="text-sm text-gray-500">{course.code}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 capitalize">{capitalizeValue(course.type)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getDifficultyColor(course.difficulty)}`}>
                            {getDifficultyDisplay(course.difficulty)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDuration(course.duration)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(course.status)}`}>
                            {getStatusDisplay(course.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{course.enrolledCount}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{course.createdAt ? formatDate(course.createdAt, { includeTime: true }) : 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <PermissionGate permission="training" action="update">
                              <button
                                onClick={() => handleEditCourse(course)}
                                className="text-[#0F2942] hover:text-blue-800 transition-colors"
                                title="Edit Course"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="training" action="read">
                              <button
                                onClick={() => handleViewCourse(course.id)}
                                className="text-brand hover:text-blue-800 transition-colors"
                                title="Preview Course"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="training" action="update">
                              <button
                                onClick={() => handleManageStructure(course.id)}
                                className="text-indigo-600 hover:text-indigo-800 transition-colors"
                                title="Manage Structure"
                              >
                                <Settings className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            {course.status === 'published' ? (
                              <PermissionGate permission="training" action="update">
                                <button
                                  onClick={() => handleUnpublishCourse(course.id)}
                                  className="text-yellow-600 hover:text-yellow-800 transition-colors"
                                  title="Unpublish"
                                  aria-label="Unpublish"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            ) : (
                              <PermissionGate permission="training" action="update">
                                <button
                                  onClick={() => handlePublishCourse(course.id)}
                                  className="text-brand hover:text-blue-800 transition-colors"
                                  title="Publish"
                                  aria-label="Publish"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                            <PermissionGate permission="training" action="delete">
                              <button
                                onClick={() => handleDeleteCourse(course.id)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Delete Course"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!loading && courses && courses.length === 0 && (
        <div className="text-center py-12">
          <div className="mx-auto h-24 w-24 text-gray-400 mb-4">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || Object.values(selectedFilters).some(filter => filter !== 'all')
              ? 'Try adjusting your search or filters to find courses.'
              : 'Get started by creating your first course.'
            }
          </p>
          <PermissionGate permission="training" action="create">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand hover:bg-brand-dark outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Course
            </button>
          </PermissionGate>
        </div>
      )}

      {/* Create/Edit Course Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="modal-content bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {showCreateModal ? 'Create New Course' : 'Edit Course'}
                </h2>
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(false);
                    setShowEditModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>


            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Course Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      data-field="title"
                      value={courseForm.title}
                      maxLength={100}
                      required
                      onKeyPress={handleTextKeyPress}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Filter out invalid characters (alphanumeric, spaces, commas only)
                        value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                        // Enforce max length
                        if (value.length > 100) {
                          value = value.substring(0, 100);
                        }
                        setCourseForm(prev => ({ ...prev, title: value }));

                        if (touched.title) {
                          const error = validateField('title', value);
                          setValidationErrors(prev => ({ ...prev, title: error }));
                        }
                      }}
                      onBlur={handleBlur}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-0 focus:border-brand ${touched.title && validationErrors.title ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Enter course title (e.g., Advanced React Development)"
                    />
                    <div className="flex justify-between items-start mt-1">
                      <div className="flex-1">
                        {touched.title && validationErrors.title && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.title}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 ml-2">
                        {courseForm.title?.length || 0}/100
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Course Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="code"
                      data-field="code"
                      value={courseForm.code}
                      maxLength={20}
                      required
                      onKeyPress={handleCodeKeyPress}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Filter out invalid characters (alphanumeric, spaces, commas, hyphens only)
                        value = value.replace(/[^a-zA-Z0-9\s,-]/g, '');
                        // Enforce max length
                        if (value.length > 20) {
                          value = value.substring(0, 20);
                        }
                        setCourseForm(prev => ({ ...prev, code: value }));

                        if (touched.code) {
                          const error = validateField('code', value);
                          setValidationErrors(prev => ({ ...prev, code: error }));
                        }
                      }}
                      onBlur={handleBlur}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-0 focus:border-brand ${touched.code && validationErrors.code ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Enter course code"
                    />
                    <div className="flex justify-between items-start mt-1">
                      <div className="flex-1">
                        {touched.code && validationErrors.code && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.code}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 ml-2">
                        {courseForm.code?.length || 0}/20
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Summary <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="summary"
                    data-field="summary"
                    value={courseForm.summary}
                    maxLength={1500}
                    required
                    onKeyPress={handleTextKeyPress}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Filter out invalid characters (alphanumeric, spaces, commas only)
                      value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                      // Enforce max length
                      if (value.length > 1500) {
                        value = value.substring(0, 1500);
                      }
                      setCourseForm(prev => ({ ...prev, summary: value }));

                      if (touched.summary) {
                        const error = validateField('summary', value);
                        setValidationErrors(prev => ({ ...prev, summary: error }));
                      }
                    }}
                    onBlur={handleBlur}
                    rows={3}
                    className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-0 focus:border-brand resize-none ${touched.summary && validationErrors.summary ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter course summary (brief description of what students will learn)"
                  />
                  <div className="flex justify-between items-start mt-1">
                    <div className="flex-1">
                      {touched.summary && validationErrors.summary && (
                        <p className="text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {validationErrors.summary}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 ml-2">
                      {courseForm.summary?.length || 0}/1500
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Learning Objectives <span className="text-red-500">*</span>
                  </label>
                  {courseForm.objectives.map((objective, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={objective}
                        maxLength={200}
                        onKeyPress={handleTextKeyPress}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Filter out invalid characters (alphanumeric, spaces, commas only)
                          value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                          // Enforce max length
                          if (value.length > 200) {
                            value = value.substring(0, 200);
                          }
                          const newObjectives = [...courseForm.objectives];
                          newObjectives[index] = value;
                          setCourseForm(prev => ({ ...prev, objectives: newObjectives }));

                          // Real-time validation for learning objectives
                          let error = null;
                          if (value && value.trim().length > 0) {
                            if (value.trim().length < 5) {
                              error = 'Each learning objective must be at least 5 characters long';
                            } else if (value.trim().length > 200) {
                              error = 'Each learning objective cannot exceed 200 characters';
                            }
                          }

                          if (touched.objectives) {
                            const validationError = validateField('objectives', newObjectives);
                            setValidationErrors(prev => ({ ...prev, objectives: validationError }));
                          }
                        }}
                        onBlur={() => {
                          setTouched(prev => ({ ...prev, objectives: true }));
                          const error = validateField('objectives', courseForm.objectives);
                          setValidationErrors(prev => ({ ...prev, objectives: error }));
                        }}
                        className={`flex-1 p-2 border rounded-lg focus:outline-none focus:ring-0 focus:border-brand ${touched.objectives && validationErrors.objectives ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter learning objective"
                      />
                      {courseForm.objectives.length > 1 && (
                        <PermissionGate permission="training" action="update">
                          <button
                            onClick={() => {
                              const newObjectives = courseForm.objectives.filter((_, i) => i !== index);
                              setCourseForm(prev => ({ ...prev, objectives: newObjectives }));
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      )}
                    </div>
                  ))}
                  <PermissionGate permission="training" action="update">
                    <button
                      onClick={() => {
                        if (courseForm.objectives.length < 10) {
                          setCourseForm(prev => ({ ...prev, objectives: [...prev.objectives, ''] }));
                        } else {
                          alert('Maximum 10 learning objectives allowed');
                        }
                      }}
                      disabled={courseForm.objectives.length >= 10}
                      className={`text-sm ${courseForm.objectives.length >= 10
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-brand hover:text-brand-dark'
                        }`}
                    >
                      + Add Objective {courseForm.objectives.length >= 10 && '(Max 10)'}
                    </button>
                  </PermissionGate>
                  {courseForm.objectives.length >= 10 && (
                    <p className="mt-1 text-xs text-gray-500">
                      Maximum 10 objectives reached
                    </p>
                  )}
                  {touched.objectives && validationErrors.objectives && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {validationErrors.objectives}
                    </p>
                  )}
                </div>
              </div>

              {/* Course Details */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Course Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prerequisites</label>
                    <input
                      type="text"
                      value={courseForm.prerequisites}
                      maxLength={300}
                      onKeyPress={handleTextKeyPress}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Filter out invalid characters (alphanumeric, spaces, commas only)
                        value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                        // Enforce max length
                        if (value.length > 300) {
                          value = value.substring(0, 300);
                        }
                        setCourseForm(prev => ({ ...prev, prerequisites: value }));
                        if (touched.prerequisites) {
                          const error = validateField('prerequisites', value);
                          setValidationErrors(prev => ({ ...prev, prerequisites: error }));
                        }
                      }}
                      onBlur={handleBlur}
                      name="prerequisites"
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-0 focus:border-brand ${touched.prerequisites && validationErrors.prerequisites ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="e.g., Basic knowledge required"
                    />
                    <div className="flex justify-between items-start mt-1">
                      <div className="flex-1">
                        {touched.prerequisites && validationErrors.prerequisites && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {validationErrors.prerequisites}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 ml-2">
                        {courseForm.prerequisites?.length || 0}/300
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Difficulty Level <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="difficulty"
                      data-field="difficulty"
                      value={courseForm.difficulty}
                      onChange={(e) => {
                        setCourseForm(prev => ({ ...prev, difficulty: e.target.value }));
                        if (touched.difficulty) {
                          const error = validateField('difficulty', e.target.value);
                          setValidationErrors(prev => ({ ...prev, difficulty: error }));
                        }
                      }}
                      onBlur={handleBlur}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-0 focus:border-brand ${touched.difficulty && validationErrors.difficulty ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Select difficulty</option>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                    {touched.difficulty && validationErrors.difficulty && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {validationErrors.difficulty}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Course Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="courseType"
                      data-field="courseType"
                      value={courseForm.courseType}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedName = courseTypeList.find(t => (t.id || t._id) === selectedId)?.name || '';
                        setCourseForm(prev => ({
                          ...prev,
                          courseType: selectedId,
                          type: selectedName
                        }));
                        if (touched.courseType) {
                          const error = validateField('courseType', selectedId);
                          setValidationErrors(prev => ({ ...prev, courseType: error }));
                        }
                      }}
                      onBlur={handleBlur}
                      disabled={loadingCourseTypes}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-0 focus:border-brand ${touched.courseType && validationErrors.courseType ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="">{loadingCourseTypes ? 'Loading course types...' : 'Select Course Type'}</option>
                      {courseTypeList.map((type) => (
                        <option key={type.id || type._id} value={type.id || type._id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                    {touched.courseType && validationErrors.courseType && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {validationErrors.courseType}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <PermissionGate permission="training" action="read">
                  <button
                    onClick={handlePreviewCourse}
                    className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-blue-50/30 transition-colors"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </button>
                </PermissionGate>
                {!showCreateModal && (
                  <PermissionGate permission="training" action="update">
                    <button
                      onClick={() => {
                        handleSaveCourse();
                        handlePublishCourse(selectedCourse.id);
                      }}
                      className="flex items-center px-4 py-2 bg-[#0F2942] text-white rounded-lg hover:bg-[#0B2033] transition-colors"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Publish
                    </button>
                  </PermissionGate>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(false);
                    setShowEditModal(false);
                  }}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <PermissionGate permission="training" action={showCreateModal ? "create" : "update"}>
                  <button
                    onClick={handleSaveCourse}
                    className="btn-create"
                  >
                    <Save className="h-4 w-4 mr-2 inline" />
                    {showCreateModal ? 'Save Draft' : 'Save Changes'}
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
          <div className="relative top-10 mx-auto p-6 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-xl rounded-lg bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Eye className="h-6 w-6 mr-2 text-[#0F2942]" />
                  Course Preview
                </h3>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-8">
                {/* Basic Information */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BookOpen className="h-5 w-5 mr-2 text-[#0F2942]" />
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Course Title</label>
                      <p className="text-sm text-gray-900 font-medium capitalize break-words">{courseForm.title || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Course Code</label>
                      <p className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">{courseForm.code || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {courseForm.type ? capitalizeFirst(courseForm.type.replace(/_/g, ' ')) : 'Not specified'}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${courseForm.difficulty === 'beginner' ? 'bg-blue-100 text-blue-800' :
                        courseForm.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                          courseForm.difficulty === 'advanced' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {courseForm.difficulty ? capitalizeFirst(courseForm.difficulty) : 'Not specified'}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${courseForm.status === 'published' ? 'bg-blue-100 text-blue-800' :
                        courseForm.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                          courseForm.status === 'archived' ? 'bg-gray-100 text-gray-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                        {courseForm.status === 'published' ? 'Published' :
                          courseForm.status === 'draft' ? 'Draft' :
                            courseForm.status === 'archived' ? 'Archived' :
                              courseForm.status || 'Not specified'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-[#0F2942]" />
                    Description
                  </h4>
                  <p className="text-sm text-gray-900 leading-relaxed capitalize break-words">{courseForm.summary || 'No description provided'}</p>
                </div>

                {/* Learning Objectives */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Target className="h-5 w-5 mr-2 text-[#0F2942]" />
                    Learning Objectives
                  </h4>
                  <ul className="space-y-2">
                    {courseForm.objectives?.length > 0 && courseForm.objectives.some(obj => obj.trim()) ? (
                      courseForm.objectives.filter(obj => obj.trim()).map((objective, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="h-4 w-4 text-brand mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-900 capitalize line-clamp-2 break-words flex-1">{objective}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-sm text-gray-500 italic">No objectives specified</li>
                    )}
                  </ul>
                </div>

                {/* Prerequisites */}
                {courseForm.prerequisites && (
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-[#0F2942]" />
                      Prerequisites
                    </h4>
                    <p className="text-sm text-gray-900 capitalize line-clamp-3 break-words">{courseForm.prerequisites}</p>
                  </div>
                )}

                {/* Modules & Lessons */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BookOpen className="h-5 w-5 mr-2 text-[#0F2942]" />
                    Course Structure
                  </h4>
                  {previewCourseModules && previewCourseModules.length > 0 ? (
                    <div className="space-y-4">
                      {previewCourseModules.map((module, moduleIndex) => (
                        <div key={module.id || module._id || moduleIndex} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0">
                              <h5 className="text-base font-semibold text-gray-900 mb-1 capitalize line-clamp-2 break-words">
                                Module {moduleIndex + 1}: {module.title || 'Untitled Module'}
                              </h5>
                              {module.description && (
                                <p className="text-sm text-gray-600 mt-1 capitalize line-clamp-2 break-words">{module.description}</p>
                              )}
                            </div>

                          </div>

                          {module.lessons && module.lessons.length > 0 ? (
                            <div className="mt-3 space-y-2">
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                                Lessons ({module.lessons.length})
                              </p>
                              <div className="space-y-2">
                                {module.lessons.map((lesson, lessonIndex) => (
                                  <div key={lesson.id || lesson.lessonId || lessonIndex} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                                    {/* Lesson Header */}
                                    <div className="flex items-start space-x-3">
                                      <div className="flex-shrink-0 mt-0.5">
                                        {lesson.type === 'video' ? (
                                          <Video className="h-4 w-4 text-[#0F2942]" />
                                        ) : lesson.type === 'youtube' ? (
                                          <Video className="h-4 w-4 text-red-500" />
                                        ) : lesson.type === 'file' || lesson.type === 'document' ? (
                                          <FileText className="h-4 w-4 text-purple-600" />
                                        ) : lesson.type === 'quiz' || lesson.type === 'assessment' ? (
                                          <CheckCircle className="h-4 w-4 text-orange-600" />
                                        ) : (
                                          <FileText className="h-4 w-4 text-gray-600" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-2">
                                          <p className="text-sm font-medium text-gray-900 capitalize break-words flex-1">
                                            {lessonIndex + 1}. {lesson.title || 'Untitled Lesson'}
                                          </p>
                                          {lesson.isGraded && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">
                                              Graded
                                            </span>
                                          )}
                                        </div>
                                        {lesson.description && (
                                          <p className="text-xs text-gray-600 mt-1 line-clamp-2 capitalize break-words">{lesson.description}</p>
                                        )}
                                        {/* Badges row */}
                                        <div className="flex items-center flex-wrap gap-1 mt-1">
                                          {/* Video badge + inline play button */}
                                          {lesson.type === 'video' && (
                                            <>
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-[#0B2033]">
                                                <Video className="h-3 w-3 mr-1" /> Video
                                              </span>
                                              {lesson.files && lesson.files.find(f => f.type === 'video' && f.url) && (
                                                <button
                                                  onClick={() => setPlayingLesson({ title: lesson.title, type: 'video', url: lesson.files.find(f => f.type === 'video' && f.url).url })}
                                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-[#0F2942] hover:bg-[#0B2033] text-white transition-colors"
                                                  title="Play video"
                                                >
                                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                                  Play
                                                </button>
                                              )}
                                            </>
                                          )}
                                          {/* YouTube badge + inline play + open link */}
                                          {lesson.type === 'youtube' && lesson.files && (() => {
                                            const ytFile = lesson.files.find(f => f.type === 'youtube');
                                            if (!ytFile || !ytFile.url) return (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                                <Video className="h-3 w-3 mr-1" /> YouTube
                                              </span>
                                            );
                                            let videoId = null;
                                            try {
                                              const u = new URL(ytFile.url);
                                              videoId = u.hostname.includes('youtu.be') ? u.pathname.slice(1) : u.searchParams.get('v');
                                            } catch (_) { }
                                            return (
                                              <>
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                                  <Video className="h-3 w-3 mr-1" /> YouTube
                                                </span>
                                                <a
                                                  href={ytFile.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-600 hover:bg-red-700 text-white transition-colors"
                                                  title="Open in YouTube"
                                                  onClick={e => e.stopPropagation()}
                                                >
                                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                                  Play
                                                </a>
                                              </>
                                            );
                                          })()}
                                          {(lesson.type === 'file' || lesson.type === 'document') && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                              <FileText className="h-3 w-3 mr-1" /> Document
                                            </span>
                                          )}
                                          {lesson.duration > 0 && (lesson.type === 'video' || lesson.type === 'youtube') && (
                                            <span className="flex items-center text-xs text-gray-500">
                                              <Clock className="h-3 w-3 mr-1" />
                                              {lesson.duration} min
                                            </span>
                                          )}
                                          {/* Document download link */}
                                          {(lesson.type === 'file' || lesson.type === 'document') && lesson.files && lesson.files.find(f => f.type === 'file') && lesson.files.find(f => f.type === 'file').url && (
                                            <a
                                              href={lesson.files.find(f => f.type === 'file').url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center text-xs text-purple-600 hover:text-purple-800 underline"
                                              onClick={e => e.stopPropagation()}
                                            >
                                              <Download className="h-3 w-3 mr-1" /> View Doc
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic mt-2">No lessons in this module</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No modules added to this course yet</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    setPreviewCourseModules([]);
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Video Player Modal ── */}
      {playingLesson && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-80 !mt-0"
          onClick={() => setPlayingLesson(null)}
        >
          <div
            className="relative w-full max-w-3xl mx-4 bg-black rounded-2xl overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-3 bg-gray-900">
              <div className="flex items-center space-x-2">
                {playingLesson.type === 'youtube' ? (
                  <Video className="h-5 w-5 text-red-400" />
                ) : (
                  <Video className="h-5 w-5 text-blue-400" />
                )}
                <span className="text-white font-medium text-sm truncate max-w-xs">
                  {playingLesson.title}
                </span>
              </div>
              <button
                onClick={() => setPlayingLesson(null)}
                className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Video content */}
            {playingLesson.type === 'video' && (
              <video
                controls
                autoPlay
                className="w-full bg-black"
                style={{ maxHeight: '520px' }}
                src={playingLesson.url}
              >
                Your browser does not support the video tag.
              </video>
            )}

            {playingLesson.type === 'youtube' && playingLesson.videoId && (
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <iframe
                  src={`https://www.youtube.com/embed/${playingLesson.videoId}?autoplay=1`}
                  title={playingLesson.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                />
              </div>
            )}

            {playingLesson.type === 'youtube' && !playingLesson.videoId && (
              <div className="p-8 text-center text-gray-400">
                <p>Could not embed this YouTube video.</p>
                <a
                  href={playingLesson.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center text-red-400 hover:text-red-300 underline text-sm"
                >
                  <Link className="h-4 w-4 mr-1" /> Open in YouTube
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pagination */}
      {shouldShowPagination && (
        <PaginationControls
          currentPage={pagination.currentPage || 1}
          totalPages={pagination.totalPages || 1}
          totalItems={pagination.totalCourses || 0}
          limit={effectivePaginationLimit}
          hasNextPage={pagination.hasNextPage !== false && (pagination.currentPage || 1) < (pagination.totalPages || 1)}
          hasPrevPage={pagination.hasPrevPage !== false && (pagination.currentPage || 1) > 1}
          onPageChange={handlePageChange}
          onLimitChange={handleItemsPerPageChange}
          limitOptions={[10, 25, 50, 100]}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirm Action
                </h3>
              </div>
              <button
                onClick={cancelDeleteCourse}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={loading}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 break-words leading-relaxed">
                {isBulkDelete
                  ? `Are you sure you want to delete ${selectedRows.size} selected course(s)? This action cannot be undone.`
                  : <>Are you sure you want to delete the course <span className="font-medium break-words">{courses?.find(c => c.id === courseToDelete)?.title || 'this course'}</span>? This action cannot be undone.</>
                }
              </p>
            </div>

            <div className="flex space-x-3 justify-end">
              <button
                onClick={cancelDeleteCourse}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-blue-50/30 transition-colors"
                disabled={loading}
              >
                No
              </button>
              <button
                onClick={confirmDeleteCourse}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unpublish Confirmation Modal */}
      {showUnpublishConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirm Unpublish
                </h3>
              </div>
              <button
                onClick={cancelUnpublishCourse}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={loading}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 break-words leading-relaxed">
                This course is currently assigned to employees. Do you still want to unpublish it?
              </p>
            </div>

            <div className="flex space-x-3 justify-end">
              <button
                onClick={cancelUnpublishCourse}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-blue-50/30 transition-colors"
                disabled={loading}
              >
                No
              </button>
              <button
                onClick={confirmUnpublishCourse}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        scopedFormatExport={{
          onExport: handleScopedExport,
        }}
        loading={exportLoading}
        title="Export Learning Catalog"
      />
    </div>
  );
};

export default TrainingCatalog;
